import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Target, Users } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/Button';
import { button } from '@/components/button-variants';
import { CampaignBlockers, CampaignSteps } from '@/components/CampaignSteps';
import { Field, fieldInputClass } from '@/components/Field';
import { CampaignStatusPill } from '@/components/StatusPill';
import { EmptyState, ErrorState, Skeleton } from '@/components/States';
import { useBrand } from '@/hooks/use-brands';
import { useCampaign } from '@/hooks/use-campaigns';
import { useSetAudience } from '@/hooks/use-campaign-flow';
import { useChecklist } from '@/hooks/use-send';
import { useSegments } from '@/hooks/use-segments';
import { describeError } from '@/lib/describe-error';

/**
 * Step 2 — who the campaign goes to (spec § 4.5).
 *
 * This screen did not exist, and neither did the endpoint behind it: `Campaign.SegmentId` was
 * writable only by the clone path, so no campaign a user created could ever have an audience, and the
 * pre-send checklist blocked on it permanently. Everything downstream — render, freeze, dispatch —
 * was built and tested, and unreachable.
 *
 * ── EXCLUSION IS A SET DIFFERENCE, AND THE UI SAYS SO ─────────────────────────────
 *
 * The backend resolves A minus B as a genuine difference rather than a fused `A AND NOT B`, precisely
 * so it can report how many the exclusion removed. That number is shown here, because "send to
 * Everyone except Recently mailed" is a decision somebody wants to see the size of before committing
 * to it — and a silent exclusion that removes 90% of the audience is the kind of thing discovered
 * after a send.
 *
 * ── THE COUNTS COME FROM THE CHECKLIST, NOT FROM THE SEGMENT ──────────────────────
 *
 * A segment carries a cached count. The checklist recomputes the audience from scratch, after
 * suppression and dedupe. Those two numbers differ and the checklist's is the true one, so it is the
 * one shown — a screen that promises 48,000 recipients and a send that mails 41,000 is a screen that
 * lied, even though the segment's own count was accurate about the segment.
 */
export function AudiencePage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const resolvedId = campaignId ?? '';

  const campaign = useCampaign(resolvedId);
  const segments = useSegments();
  const checklist = useChecklist(resolvedId);
  const brand = useBrand(campaign.data?.brandId);
  const save = useSetAudience(resolvedId);

  const [segmentId, setSegmentId] = useState('');
  const [excludeSegmentId, setExcludeSegmentId] = useState('');
  const [topicId, setTopicId] = useState('');

  // Adopt the server's values once the campaign loads. An effect because it synchronises local form
  // state with a fetch that completes later; keyed on the ids so a save does not clobber a change the
  // user made while it was in flight.
  useEffect(() => {
    if (campaign.data === undefined) return;

    setSegmentId(campaign.data.segmentId ?? '');
    setExcludeSegmentId(campaign.data.excludeSegmentId ?? '');
    setTopicId(campaign.data.topicId ?? '');
  }, [
    campaign.data?.segmentId,
    campaign.data?.excludeSegmentId,
    campaign.data?.topicId,
    campaign.data,
  ]);

  if (campaign.isPending) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-48 w-full max-w-2xl" />
      </div>
    );
  }

  if (campaign.isError) {
    return <ErrorState error={campaign.error} onRetry={() => void campaign.refetch()} />;
  }

  const detail = campaign.data;
  const available = segments.data?.items ?? [];

  const isEditable = detail.status === 'Draft' || detail.status === 'InReview';
  const isDirty =
    segmentId !== (detail.segmentId ?? '') ||
    excludeSegmentId !== (detail.excludeSegmentId ?? '') ||
    topicId !== (detail.topicId ?? '');

  const sameSegment = segmentId.length > 0 && segmentId === excludeSegmentId;

  const audienceItem = checklist.data?.items.find((item) => item.key === 'audience');

  // Retired topics are excluded from the picker but stay resolvable in the payload, so a campaign that
  // declared one before it was retired still shows its name rather than an empty select.
  const activeTopics = (brand.data?.topics ?? []).filter(
    (topic) => topic.isActive || topic.id === detail.topicId,
  );

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (segmentId.length === 0 || sameSegment) return;

    save.mutate({
      segmentId,
      excludeSegmentId: excludeSegmentId.length === 0 ? null : excludeSegmentId,
      topicId: topicId.length === 0 ? null : topicId,
    });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="shrink-0 border-b border-border bg-surface px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/campaigns"
            className="flex items-center gap-1.5 text-sm text-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Campaigns
          </Link>

          <span className="text-border" aria-hidden="true">
            /
          </span>

          <h1 className="truncate text-sm font-semibold text-heading">{detail.name}</h1>

          <CampaignStatusPill status={detail.status} />

          <div className="ml-auto">
            <CampaignBlockers
              blockingCount={checklist.data?.blockingCount}
              isLoading={checklist.isPending}
            />
          </div>
        </div>

        <div className="mt-2">
          <CampaignSteps campaign={detail} />
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-heading">
            <Target className="size-5 text-primary" aria-hidden="true" />
            Who gets this?
          </h2>

          <p className="mt-1 text-sm text-muted">
            Pick a segment to send to. Suppressed and unsubscribed contacts are removed automatically —
            you never have to exclude them by hand.
          </p>

          {segments.isPending ? (
            <Skeleton className="mt-6 h-40 w-full" />
          ) : segments.isError ? (
            <ErrorState error={segments.error} onRetry={() => void segments.refetch()} />
          ) : available.length === 0 ? (
            <div className="mt-6">
              <EmptyState
                icon={Users}
                title="No segments yet"
                description="A segment is a saved rule describing a group of contacts — everyone in the north, everyone who clicked last month. You need one before a campaign can be sent."
                action={
                  <Link to="/segments" className={button({ variant: 'primary', size: 'sm' })}>
                    Build a segment
                  </Link>
                }
              />
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4" noValidate>
              {!isEditable && (
                <p className="rounded-md bg-warning-bg px-3 py-2 text-sm text-warning-ink" role="status">
                  This campaign is {detail.status.toLowerCase()}, so its audience is fixed. An approval
                  is given against a specific audience — change it and the approval would no longer
                  describe what goes out.
                </p>
              )}

              {save.isError && (
                <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink" role="alert">
                  {describeError(save.error).message}
                </p>
              )}

              <Field
                id="segment"
                label="Send to"
                hint="The group this campaign goes to."
              >
                <select
                  id="segment"
                  value={segmentId}
                  disabled={!isEditable}
                  onChange={(event) => setSegmentId(event.target.value)}
                  aria-describedby="segment-hint"
                  className={fieldInputClass}
                >
                  <option value="">Choose a segment…</option>
                  {available.map((segment) => (
                    <option key={segment.id} value={segment.id}>
                      {segment.name}
                      {segment.lastCount === null ? '' : ` — about ${segment.lastCount.toLocaleString()}`}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                id="exclude"
                label="But not"
                hint="Optional. Everyone in this segment is removed from the send — useful for people you mailed recently."
                error={sameSegment ? 'That is the same segment you are sending to.' : undefined}
              >
                <select
                  id="exclude"
                  value={excludeSegmentId}
                  disabled={!isEditable}
                  onChange={(event) => setExcludeSegmentId(event.target.value)}
                  aria-invalid={sameSegment}
                  aria-describedby={sameSegment ? 'exclude-error' : 'exclude-hint'}
                  className={fieldInputClass}
                >
                  <option value="">No exclusion</option>
                  {available
                    .filter((segment) => segment.id !== segmentId)
                    .map((segment) => (
                      <option key={segment.id} value={segment.id}>
                        {segment.name}
                      </option>
                    ))}
                </select>
              </Field>

              {/* The topic. Placed after the segments because it narrows what they matched, and its
                  hint says what declaring one actually does — otherwise it reads as a label. */}
              <Field
                id="topic"
                label="What is this about?"
                hint={
                  activeTopics.length === 0
                    ? 'This brand has no topics. An administrator adds them, and without one a campaign is not subject to per-person topic preferences.'
                    : 'Optional. Declaring a topic excludes anyone who turned it off in the preference centre, and holds back anyone at the monthly limit they set for it.'
                }
              >
                <select
                  id="topic"
                  value={topicId}
                  disabled={!isEditable || activeTopics.length === 0}
                  onChange={(event) => setTopicId(event.target.value)}
                  aria-describedby="topic-hint"
                  className={fieldInputClass}
                >
                  <option value="">No topic — preferences do not apply</option>
                  {activeTopics.map((topic) => (
                    <option key={topic.id} value={topic.id}>
                      {topic.name}
                    </option>
                  ))}
                </select>
              </Field>

              {isEditable && (
                <div className="flex items-center gap-3">
                  <Button type="submit" variant="primary" disabled={save.isPending || !isDirty}>
                    {save.isPending ? 'Saving…' : 'Save audience'}
                  </Button>

                  {/* Only offered once there is something to review. Before that it would send
                      somebody to a checklist whose first line is the thing they are still doing. */}
                  {detail.segmentId !== null && !isDirty && (
                    <Link
                      to={`/campaigns/${detail.id}/send`}
                      className={button({ variant: 'secondary' })}
                    >
                      Review and send
                      <ArrowRight className="size-4" aria-hidden="true" />
                    </Link>
                  )}
                </div>
              )}
            </form>
          )}

          {/* The resolved size, from the checklist. Shown after the form because it is a consequence
              of it rather than an input to it. */}
          {detail.segmentId !== null && (
            <div className="mt-8 rounded-lg border border-border bg-surface p-4">
              <h3 className="text-sm font-semibold text-heading">After suppression and dedupe</h3>

              {checklist.isPending ? (
                <Skeleton className="mt-2 h-6 w-32" />
              ) : checklist.isError ? (
                <p className="mt-2 text-sm text-muted">
                  The audience size could not be resolved just now. The review step will recompute it.
                </p>
              ) : (
                <>
                  <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                    {checklist.data.audienceSize.toLocaleString()}
                    <span className="ml-2 text-sm font-normal text-muted">
                      {checklist.data.audienceSize === 1 ? 'recipient' : 'recipients'}
                    </span>
                  </p>

                  {/* The DETAIL, not the summary. The summary repeats the figure above it; the detail
                      carries the breakdown — how many the exclusion removed, how many turned the topic
                      off, how many are at their own limit. Choosing a topic can drop an audience by
                      thousands, and this is the screen where that choice is made, so the reason belongs
                      here rather than one step further on. */}
                  {audienceItem?.detail !== undefined && audienceItem.detail !== null && (
                    <p className="mt-2 text-sm text-muted">{audienceItem.detail}</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
